/**
 * Shared, context-free atoms used across every surface.
 *
 * RULE FOR EXTRACTION: read from here freely, but do NOT add feature-specific
 * copy. Only add a key here if an editor would always want to change every
 * occurrence together (generic buttons, generic errors, units). Anything
 * sentence-length or feature-flavored belongs in its feature namespace, even if
 * the English is byte-identical today — those phrasings diverge across locales.
 */
export const common = {
  appName: "OmniMedia",

  // Generic actions (buttons, menu items).
  action: {
    save: "Save",
    saveChanges: "Save changes",
    cancel: "Cancel",
    delete: "Delete",
    remove: "Remove",
    edit: "Edit",
    add: "Add",
    create: "Create",
    update: "Update",
    close: "Close",
    confirm: "Confirm",
    back: "Back",
    next: "Next",
    previous: "Previous",
    done: "Done",
    continue: "Continue",
    retry: "Retry",
    refresh: "Refresh",
    search: "Search",
    filter: "Filter",
    clear: "Clear",
    select: "Select",
    selectAll: "Select all",
    apply: "Apply",
    submit: "Submit",
    signOut: "Sign out",
    copy: "Copy",
    copied: "Copied",
    upload: "Upload",
    download: "Download",
    learnMore: "Learn more",
    viewAll: "View all",
    open: "Open",
  },

  // Generic status / feedback.
  status: {
    loading: "Loading…",
    saving: "Saving…",
    saved: "Saved",
    deleting: "Deleting…",
    uploading: "Uploading…",
    processing: "Processing…",
    somethingWentWrong: "Something went wrong",
    somethingWentWrongRetry: "Something went wrong. Please try again.",
    noResults: "No results",
    none: "None",
    active: "Active",
    inactive: "Inactive",
    online: "Online",
    offline: "Offline",
    enabled: "Enabled",
    disabled: "Disabled",
    yes: "Yes",
    no: "No",
    optional: "Optional",
    required: "Required",
  },

  // Generic field labels reused verbatim across many forms.
  field: {
    name: "Name",
    email: "Email",
    password: "Password",
    description: "Description",
    timezone: "Timezone",
    type: "Type",
    status: "Status",
  },

  // Locale-aware time field (components/ui/time-input.tsx). The placeholder
  // shows the format the field expects in the visitor's locale.
  timeInput: {
    placeholder24h: "HH:MM",
    placeholder12h: "h:mm AM",
    invalid: "Enter a time like {example}",
  },

  // Generic validation messages.
  validation: {
    nameRequired: "Name is required",
    emailRequired: "Email is required",
    emailInvalid: "Enter a valid email address",
    required: "{field} is required",
  },

  // Generic count nouns as plural forms — render "1 device" / "5 devices".
  count: {
    devices: { one: "{count} device", other: "{count} devices" },
    locations: { one: "{count} location", other: "{count} locations" },
    channels: { one: "{count} channel", other: "{count} channels" },
    items: { one: "{count} item", other: "{count} items" },
    tracks: { one: "{count} track", other: "{count} tracks" },
    members: { one: "{count} member", other: "{count} members" },
    selected: { one: "{count} selected", other: "{count} selected" },
  },

  // Separator for joining inline lists ("a, b, c"). Locale-specific later.
  listSeparator: ", ",
} as const;
